/**
 * KioskCheckIn — visitor check-in form (ADR-073 Decision 5).
 *
 * Supports two modes:
 * - scan: ID scanning via camera (Regula WASM SDK placeholder)
 * - manual: Manual name/DOB entry
 *
 * After entering info, the visitor selects a visit reason and host,
 * then submits. The backend handles screening, watch list, and SIS match.
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Center,
  Field,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Button as ButtonComponent, CardButton, SelectInput } from '@themes/aegix';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useKiosk } from '../../contexts/KioskContext';
import {
  useCheckIn,
  useReasonTypes,
  useVisitorSearch,
} from '../../hooks/queries/useVisitorManagement';
import type { CheckInRequest } from '../../types/VisitorManagementTypes';
import { toaster } from '../../components/ui/toaster';
import { HiArrowLeft } from 'react-icons/hi2';

export function KioskCheckIn() {
  const { t } = useTranslation();
  usePageTitle('Kiosk');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') ?? 'manual';
  const { schoolId, buildingId } = useKiosk();

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [hostName, setHostName] = useState('');
  const [destination, setDestination] = useState('');
  const [notes, setNotes] = useState('');

  // Returning visitor search
  const searchQuery = firstName && lastName ? `${firstName} ${lastName}` : '';
  const { data: searchResults } = useVisitorSearch(searchQuery);
  const [selectedVisitorId, setSelectedVisitorId] = useState<string | null>(
    null,
  );

  // Reason types
  const { data: reasonTypes } = useReasonTypes();
  const reasonOptions = (reasonTypes ?? [])
    .filter((r) => r.is_active)
    .map((r) => ({ label: r.name, value: r.code }));

  const selectedReason = reasonTypes?.find((r) => r.code === reasonCode);

  // Check-in mutation
  const checkInMutation = useCheckIn();

  // If scan mode, show placeholder for Regula SDK
  const isScanMode = mode === 'scan';

  // Clear visitor selection when name changes
  useEffect(() => {
    setSelectedVisitorId(null);
  }, [firstName, lastName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!schoolId) {
      toaster.create({ title: 'No school configured', type: 'error' });
      return;
    }

    const payload: CheckInRequest = {
      school: schoolId,
      building: buildingId ?? undefined,
      is_kiosk_checkin: true,
      reason_type_code: reasonCode || undefined,
      host_name: hostName || undefined,
      destination: destination || undefined,
      notes: notes || undefined,
    };

    if (selectedVisitorId) {
      payload.visitor_public_id = selectedVisitorId;
    } else {
      payload.first_name = firstName;
      payload.last_name = lastName;
      payload.date_of_birth = dob || undefined;
    }

    checkInMutation.mutate(payload, {
      onSuccess: (result) => {
        navigate('/kiosk/result', {
          state: {
            visit: result.visit,
            badgeNumber: result.badge_number,
            screeningTriggered: result.screening_triggered,
            watchListHits: result.watch_list_hits,
            sisMatch: result.sis_match,
          },
        });
      },
      onError: (err) => {
        toaster.create({
          title: t('kiosk.errors.checkInFailed', 'Check-in failed'),
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  return (
    <Box minH="100vh" bg="bg.subtle" py={8} px={4}>
      <Box maxW="600px" mx="auto">
        {/* Back button */}
        <ButtonComponent
          visual="secondary"
          onClick={() => navigate('/kiosk')}
          mb={6}
          size="sm"
          minH="44px"
          minW="44px"
          data-e2e="kiosk-back-button"
        >
          <HiArrowLeft size={16} />
          <Text ml={2}>{t('common.back', 'Back')}</Text>
        </ButtonComponent>

        <Heading as="h1" size="lg" mb={6} textAlign="center">
          {isScanMode ? t('kiosk.scanYourId', 'Scan Your ID') : t('kiosk.visitorCheckIn', 'Visitor Check-In')}
        </Heading>

        {isScanMode && (
          <Center
            bg="bg.muted"
            borderRadius="lg"
            h="200px"
            mb={6}
            border="2px dashed"
            borderColor="border.default"
          >
            <VStack gap={2}>
              <Text color="text.secondary" fontSize="lg">
                {t('kiosk.cameraPreview', 'Camera Preview')}
              </Text>
              <Text color="text.secondary" fontSize="sm">
                {t('kiosk.idScanningRequiresLicense', 'ID scanning requires Regula SDK license')}
              </Text>
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={() => navigate('/kiosk/check-in?mode=manual')}
                  minH="44px"
                  minW="44px"
                  data-e2e="kiosk-manual-entry-button"
                >
                  {t('kiosk.enterManuallyInstead', 'Enter Manually Instead')}
                </ButtonComponent>
            </VStack>
          </Center>
        )}

        <form onSubmit={handleSubmit}>
          <VStack gap={5} align="stretch">
            {/* Name fields */}
            <SimpleGrid columns={2} gap={4}>
              <Field.Root>
                <Field.Label>{t('first_name', 'First Name')}</Field.Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('placeholders.first_name', 'First name')}
                  required
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- kiosk check-in: unattended walkup flow, focus the first required field (unless in scan mode where the scanner handles input)
                  autoFocus={!isScanMode}
                  size="lg"
                  data-e2e="kiosk-first-name-input"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('last_name', 'Last Name')}</Field.Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('placeholders.last_name', 'Last name')}
                  required
                  size="lg"
                  data-e2e="kiosk-last-name-input"
                />
              </Field.Root>
            </SimpleGrid>

            {/* Returning visitor match */}
            {searchResults &&
              searchResults.length > 0 &&
              !selectedVisitorId && (
                <Box
                  p={3}
                  bg="blue.50"
                  _dark={{ bg: 'blue.900' }}
                  borderRadius="md"
                >
                  <Text fontSize="sm" fontWeight="600" mb={2}>
                    {t('kiosk.returningVisitorNotice', 'Returning visitor? Select your name:')}
                  </Text>
                  <VStack gap={1} align="stretch">
                    {searchResults.slice(0, 3).map((v) => (
                      <CardButton
                        key={v.public_id}
                        variant="row"
                        size="lg"
                        onClick={() => {
                          setSelectedVisitorId(v.public_id);
                          setFirstName(v.first_name);
                          setLastName(v.last_name);
                        }}
                        aria-label={`Select ${v.first_name} ${v.last_name}`}
                        description={
                          v.date_of_birth ? `DOB: ${v.date_of_birth}` : undefined
                        }
                      >
                        {v.first_name} {v.last_name}
                      </CardButton>
                    ))}
                  </VStack>
                </Box>
              )}

            {selectedVisitorId && (
              <Box
                p={3}
                bg="green.50"
                _dark={{ bg: 'green.900' }}
                borderRadius="md"
              >
                <Text
                  fontSize="sm"
                  color="green.700"
                  _dark={{ color: 'green.200' }}
                >
                  {t('kiosk.welcomeBackNotice', 'Welcome back, {{name}}! Using your existing visitor profile.', { name: firstName })}
                </Text>
              </Box>
            )}

            {/* DOB (optional for new visitors) */}
            {!selectedVisitorId && (
              <Field.Root>
                <Field.Label>{t('kiosk.dobOptional', 'Date of Birth (optional)')}</Field.Label>
                <Input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  size="lg"
                  data-e2e="kiosk-dob-input"
                />
              </Field.Root>
            )}

            {/* Visit reason */}
            <SelectInput
              label={t('kiosk.reasonForVisit', 'Reason for Visit')}
              name="reason"
              value={reasonCode}
              onChange={(val: string) => setReasonCode(val)}
              option={reasonOptions}
              placeholder={t('kiosk.selectReasonPlaceholder', 'Select reason')}
              e2eTitle="kiosk-reason-select"
            />

            {/* Host (if required by reason type) */}
            {(selectedReason?.requires_host || !selectedReason) && (
              <Field.Root>
                <Field.Label>
                  {t('kiosk.whoAreYouVisiting', 'Who are you visiting?')}{' '}
                  {selectedReason?.requires_host && (
                    <Text as="span" color="text.danger">
                      *
                    </Text>
                  )}
                </Field.Label>
                <Input
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder={t('kiosk.hostNamePlaceholder', "Name of person you're visiting")}
                  required={selectedReason?.requires_host}
                  size="lg"
                  data-e2e="kiosk-host-input"
                />
              </Field.Root>
            )}

            {/* Destination */}
            <Field.Root>
              <Field.Label>{t('kiosk.destinationOptional', 'Destination (optional)')}</Field.Label>
              <Input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder={t('kiosk.destinationPlaceholder', 'e.g. Room 204, Main Office')}
                size="lg"
                data-e2e="kiosk-destination-input"
              />
            </Field.Root>

            {/* Submit */}
            <ButtonComponent
              visual="primary"
              type="submit"
              width="100%"
              size="lg"
              disabled={checkInMutation.isPending || !firstName || !lastName}
              data-e2e="kiosk-submit-button"
            >
              {checkInMutation.isPending ? (
                <>
                  <Spinner size="sm" mr={2} />
                  {t('visitors.checkingIn', 'Checking In...')}
                </>
              ) : (
                t('visitors.checkIn', 'Check In')
              )}
            </ButtonComponent>
          </VStack>
        </form>
      </Box>
    </Box>
  );
}
